import React, { useEffect, useMemo } from 'react';
import {
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  Radio,
  Switch,
  Space,
  Alert,
  Tag,
  Typography,
  Divider,
} from 'antd';
import { CopierSetting, Account } from '../../types';

const { Text } = Typography;

interface SettingFormModalProps {
  open: boolean;
  initialData?: CopierSetting | null;
  accounts: Account[];
  groups: string[];
  allSettings: CopierSetting[];
  onCancel: () => void;
  onSubmit: (data: SettingFormValues) => void;
}

export interface SettingFormValues {
  name: string;
  description?: string;
  sourceAccount: string;
  copyMode: 'EQUITY' | 'VOLUME' | 'FIXED';
  copyValue: number;
  targetType: 'ACCOUNT' | 'GROUP';
  targetAccounts: string[];
  targetGroups: string[];
  status: 'ACTIVE' | 'PAUSED';
}

const SettingFormModal: React.FC<SettingFormModalProps> = ({
  open,
  initialData,
  accounts,
  groups,
  allSettings,
  onCancel,
  onSubmit,
}) => {
  const [form] = Form.useForm<SettingFormValues>();
  const isEditMode = !!initialData;

  useEffect(() => {
    if (open) {
      if (initialData) {
        const targetType = initialData.targets[0]?.targetType || 'ACCOUNT';
        const accountTargets = initialData.targets
          .filter((t) => t.targetType === 'ACCOUNT')
          .map((t) => t.targetValue);
        const groupTargets = initialData.targets
          .filter((t) => t.targetType === 'GROUP')
          .map((t) => t.targetValue);

        form.setFieldsValue({
          name: initialData.name,
          description: initialData.description,
          sourceAccount: initialData.sourceAccount,
          copyMode: initialData.copyMode,
          copyValue: initialData.copyValue,
          targetType,
          targetAccounts: accountTargets,
          targetGroups: groupTargets,
          status: initialData.status === 'PAUSED' ? 'PAUSED' : 'ACTIVE',
        });
      } else {
        form.resetFields();
        form.setFieldsValue({
          copyMode: 'EQUITY',
          copyValue: 100,
          targetType: 'ACCOUNT',
          targetAccounts: [],
          targetGroups: [],
          status: 'ACTIVE',
        });
      }
    }
  }, [open, initialData, form]);

  const otherSettings = useMemo(
    () => allSettings.filter((s) => s.status !== 'DELETED' && s.id !== initialData?.id),
    [allSettings, initialData]
  );

  const usedAsSource = useMemo(
    () => new Set(otherSettings.map((s) => s.sourceAccount)),
    [otherSettings]
  );

  const usedAsTarget = useMemo(() => {
    const set = new Set<string>();
    otherSettings.forEach((s) => {
      s.targets
        .filter((t) => t.targetType === 'ACCOUNT')
        .forEach((t) => set.add(t.targetValue));
    });
    return set;
  }, [otherSettings]);

  const sourceOptions = accounts
    .map((a) => ({
      value: a.accountId,
      label: `${a.accountId} (${a.name || '이름 없음'}) - ${a.groupName || '그룹 없음'}`,
      disabled: !a.enabled,
    }));

  const watchedSource = Form.useWatch('sourceAccount', form);
  const targetAccountOptions = accounts
    .filter((a) => a.accountId !== watchedSource)
    .map((a) => ({
      value: a.accountId,
      label: `${a.accountId} (${a.name || '이름 없음'}) - ${a.groupName || '그룹 없음'}`,
      disabled: !a.enabled,
    }));

  const groupOptions = groups.map((g) => ({ value: g, label: g }));
  const watchedMode = Form.useWatch('copyMode', form);
  const watchedTargetType = Form.useWatch('targetType', form);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();

      if (values.targetType === 'ACCOUNT' && (!values.targetAccounts || values.targetAccounts.length === 0)) {
        form.setFields([
          { name: 'targetAccounts', errors: ['Target 계좌를 1개 이상 선택해야 합니다'] },
        ]);
        return;
      }
      if (values.targetType === 'GROUP' && (!values.targetGroups || values.targetGroups.length === 0)) {
        form.setFields([
          { name: 'targetGroups', errors: ['Target 그룹을 1개 이상 선택해야 합니다'] },
        ]);
        return;
      }

      onSubmit(values);
    } catch (error) {
      // Form validation error - automatic display
    }
  };

  return (
    <Modal
      title={isEditMode ? `Copier 설정 수정 (#${initialData?.id})` : '새 Copier 설정'}
      open={open}
      onOk={handleOk}
      onCancel={onCancel}
      width={700}
      okText={isEditMode ? '수정' : '저장'}
      cancelText="취소"
      destroyOnClose
    >
      <Form form={form} layout="vertical" requiredMark>
        <Form.Item
          name="name"
          label="설정 이름"
          rules={[
            { required: true, message: '이름을 입력하세요' },
            { max: 100, message: '100자 이내로 입력하세요' },
          ]}
        >
          <Input placeholder="예: VIP 카피 (Equity 100%)" />
        </Form.Item>

        <Form.Item name="description" label="설명 (선택)">
          <Input.TextArea rows={2} placeholder="이 설정의 목적이나 메모" maxLength={500} />
        </Form.Item>

        <Divider plain>Source</Divider>

        <Form.Item
          name="sourceAccount"
          label="Source 계좌"
          rules={[{ required: true, message: 'Source 계좌를 선택하세요' }]}
          extra="이 계좌의 거래가 다른 계좌로 복사됩니다."
        >
          <Select
            showSearch
            placeholder="계좌 검색 (ID 또는 이름)"
            options={sourceOptions}
            filterOption={(input, option) => {
              const label = String(option?.label ?? '');
              return label.toLowerCase().includes(input.toLowerCase());
            }}
          />
        </Form.Item>

        <Divider plain>복사 모드</Divider>

        <Form.Item
          name="copyMode"
          label="모드"
          rules={[{ required: true }]}
        >
          <Radio.Group>
            <Radio.Button value="EQUITY">Equity (자본 비례)</Radio.Button>
            <Radio.Button value="VOLUME">Volume (랏 비례)</Radio.Button>
            <Radio.Button value="FIXED">Fixed (고정 랏)</Radio.Button>
          </Radio.Group>
        </Form.Item>

        <Form.Item
          name="copyValue"
          label={watchedMode === 'FIXED' ? 'Value (Lot)' : 'Value (1~10000)'}
          rules={[
            { required: true, message: 'Value를 입력하세요' },
            { type: 'number', min: 0.01, message: '0.01 이상이어야 합니다' },
          ]}
          extra={
            watchedMode === 'EQUITY'
              ? '예: 100 = source equity 비율 그대로 / 50 = 그 절반'
              : watchedMode === 'VOLUME'
              ? '예: 100 = source 랏 그대로 / 50 = source의 50% 랏'
              : '예: 0.10 = 항상 0.10 lot으로 진입 (Source lot 무시)'
          }
        >
          <InputNumber
            min={0.01}
            step={watchedMode === 'FIXED' ? 0.01 : 1}
            style={{ width: 200 }}
            addonAfter={watchedMode === 'FIXED' ? 'lot' : '%'}
          />
        </Form.Item>

        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={
            watchedMode === 'EQUITY' ? (
              <Text style={{ fontSize: 13 }}>
                <strong>Equity 모드 공식:</strong> target_lot = source_lot × (target_equity ÷ source_equity) × (value ÷ 100)
              </Text>
            ) : watchedMode === 'VOLUME' ? (
              <Text style={{ fontSize: 13 }}>
                <strong>Volume 모드 공식:</strong> target_lot = source_lot × (value ÷ 100)
              </Text>
            ) : (
              <Text style={{ fontSize: 13 }}>
                <strong>Fixed 모드:</strong> target_lot = value (Source lot 무시, 항상 고정 랏으로 진입)
              </Text>
            )
          }
        />

        <Divider plain>Target</Divider>

        <Form.Item name="targetType" label="대상 방식" rules={[{ required: true }]}>
          <Radio.Group>
            <Radio.Button value="ACCOUNT">계좌 지정</Radio.Button>
            <Radio.Button value="GROUP">그룹 지정</Radio.Button>
          </Radio.Group>
        </Form.Item>

        {watchedTargetType === 'ACCOUNT' && (
          <Form.Item
            name="targetAccounts"
            label="Target 계좌 (다중 선택)"
            extra="Source와 같은 계좌는 선택 불가. 다중 setting 자유롭게 가능."
          >
            <Select
              mode="multiple"
              showSearch
              placeholder="계좌 검색 후 선택"
              options={targetAccountOptions}
              filterOption={(input, option) => {
                const label = String(option?.label ?? '');
                return label.toLowerCase().includes(input.toLowerCase());
              }}
              tagRender={(props) => (
                <Tag closable={props.closable} onClose={props.onClose} style={{ marginRight: 4 }}>
                  {props.label}
                </Tag>
              )}
            />
          </Form.Item>
        )}

        {watchedTargetType === 'GROUP' && (
          <Form.Item
            name="targetGroups"
            label="Target 그룹 (다중 선택)"
            extra="선택한 그룹에 속한 모든 활성 계좌가 자동으로 Target이 됩니다"
          >
            <Select
              mode="tags"
              placeholder="그룹 선택 또는 직접 입력 (Enter로 추가)"
              options={groupOptions}
              tokenSeparators={[',']}
              showSearch
              filterOption={(input, option) =>
                (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
              }
            />
          </Form.Item>
        )}

        <Divider plain>상태</Divider>

        <Form.Item
          name="status"
          label="활성화"
          valuePropName="checked"
          getValueProps={(value) => ({ checked: value === 'ACTIVE' })}
          getValueFromEvent={(checked) => (checked ? 'ACTIVE' : 'PAUSED')}
        >
          <Space>
            <Switch />
            <Text type="secondary">
              켜짐 = 활성 (즉시 복사 시작) / 꺼짐 = 일시중지
            </Text>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default SettingFormModal;